"use client";
import { useMerchant } from "@/app/providers/merchantProvider";
import { DottedHamburger } from "@/components/_icons/dottedHamburger";
import { SearchIcon } from "@/components/navigationBar/searchIcon";
import { Category } from "@/dto/category";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import clsx from "clsx";
import { debounce } from "lodash";
import { useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

export type SearchableCategory = Pick<Category, "id" | "name">;

// Helper component to handle bolding matches
const HighlightedText = ({
  text,
  highlight,
}: {
  text: string;
  highlight: string;
}) => {
  if (!highlight) return <>{text}</>;
  const parts = text.split(new RegExp(`(${highlight})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <strong key={`${part}-${i}`}>{part}</strong>
        ) : (
          <span key={`${part}-${i}`}>{part}</span>
        )
      )}
    </>
  );
};

export const SecondaryCarouselNavigationToggleButton = ({
  categories,
  handleLinkClick,
}: {
  categories: Category[];
  // eslint-disable-next-line no-unused-vars
  handleLinkClick: (hash: string) => void;
}) => {
  const t = useTranslations("category");

  return (
    <Popover>
      {({ close, open }) => (
        <>
          <PopoverButton
            className={clsx("relative z-10 p-2 rounded", {
              ["outline outline-[#D5D5D5]"]: open,
              ["hover:bg-[#EDEDED]"]: !open,
            })}
            data-testid={applyDataTestId(
              pom.header.navigationHorizontalScrollToggleButton
            )}
            aria-label={t("toggleSearchDescriptiveText")}
          >
            <DottedHamburger />
          </PopoverButton>

          <PopoverPanel
            transition
            className="absolute left-8 top-full z-20 mt-3 w-screen sm:max-w-[350px] overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 max-h-[500px] overflow-y-auto pb-8 pt-4"
            data-testid={applyDataTestId(pom.header.searchCategoriesPanel)}
          >
            <SearchPanelContent
              categories={categories}
              handleLinkClick={handleLinkClick}
              close={close}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

export const SearchPanelContent = ({
  categories,
  handleLinkClick,
  close,
}: {
  categories: SearchableCategory[];
  // eslint-disable-next-line no-unused-vars
  handleLinkClick: (hash: string) => void;
  close: () => void;
}) => {
  const t = useTranslations("category");
  const {
    merchant: { merchantMeta },
  } = useMerchant();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const hasSearchQuery = searchParams?.get("query") !== null;
  const merchantColor =
    merchantMeta.colorPalette?.color || cloverColorTheme.Main.primaryColor;

  const [searchQuery, setSearchQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const handleSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setSearchQuery(value);
        setActiveIndex(-1);
      }, 150),
    []
  );

  const filteredCategories = useMemo(() => {
    if (!searchQuery) return categories;
    return categories.filter((category) =>
      category.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [categories, searchQuery]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev < filteredCategories.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        if (activeIndex >= 0) {
          e.preventDefault();
          const category = filteredCategories[activeIndex];
          handleSelection(category);
        }
        break;
      case "Escape":
        e.preventDefault();
        close();
        break;
      default:
        break;
    }
  };

  const handleSelection = (category: SearchableCategory) => {
    const categoryLink = hasSearchQuery
      ? `${pathName}#${category.name}`
      : `#${category.name}`;

    if (typeof globalThis !== "undefined") {
      globalThis.location.hash = categoryLink;
    }

    handleLinkClick(categoryLink);
    close();
  };

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const activeItem = listRef.current.children[activeIndex] as HTMLElement;
      if (activeItem) {
        activeItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex]);

  return (
    <>
      <label className="w-11/12 mx-auto block mb-4">
        <div
          className="border border-[#e5e5e5] rounded-md flex flex-row items-center px-2 py-2 bg-[#f7f7f7] text-sm focus-within:ring-2 mb-0"
          style={{ "--tw-ring-color": merchantColor } as React.CSSProperties}
        >
          <SearchIcon aria-hidden="true" />
          <input
            ref={inputRef}
            autoFocus
            role="combobox"
            aria-autocomplete="list"
            aria-controls="categories-results-listbox"
            aria-activedescendant={
              activeIndex >= 0 ? `category-option-${activeIndex}` : undefined
            }
            aria-expanded
            placeholder={t("searchCategoryPlaceholder")}
            className="px-2 text-black text-base font-medium bg-inherit focus-within:outline-none w-full placeholder:text-[#5e5c5d]"
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            data-testid={applyDataTestId(
              pom.header.searchCategoriesTypeaheadInput
            )}
          />
        </div>
      </label>

      <span className="sr-only" aria-live="polite">
        {filteredCategories.length === 0
          ? t("emptyCategorySearch")
          : t("resultsFound", { count: filteredCategories.length })}
      </span>

      <ul // NOSONAR(S6819) - Required for ARIA Combobox pattern
        ref={listRef}
        className="flex flex-col px-4"
        id="categories-results-listbox"
        role="listbox" // NOSONAR(S6819) - Required for ARIA Combobox pattern
        data-testid={applyDataTestId(
          pom.header.searchCategoriesTypeaheadOptions
        )}
      >
        {filteredCategories.map((category, index) => {
          const isActive = index === activeIndex;
          return (
            <li // NOSONAR(S6819) - Required for ARIA Combobox pattern
              key={category.id}
              id={`category-option-${index}`}
              role="option" // NOSONAR(S6819) - Required for ARIA Combobox pattern
              aria-selected={isActive}
              className={clsx(
                "cursor-pointer text-base font-normal w-full block px-4 py-3 rounded-xl select-none",
                isActive ? "bg-lightGray" : "hover:bg-lightGray"
              )}
              onClick={() => handleSelection(category)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSelection(category);
              }}
            >
              <HighlightedText text={category.name} highlight={searchQuery} />
            </li>
          );
        })}
      </ul>

      {filteredCategories.length === 0 && searchQuery.length > 0 && (
        <p
          className="text-center text-sm text-gray-500 mt-6"
          data-testid={applyDataTestId(
            pom.header.searchCategoriesTypeaheadEmptyMessage
          )}
        >
          {t("emptyCategorySearch")}
        </p>
      )}
    </>
  );
};
